import React from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar } from 'lucide-react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';

const AbsenceBalanceAccordion = ({ formData, updateField }) => {
  const { t } = useTranslation();
  const unitLabel = formData.unit === 'DAY' ? t('common.days') : t('common.hours');

  return (
  <AccordionItem value="balance">
    <AccordionTrigger>
      <div className="flex items-center gap-2">
        <Calendar className="h-5 w-5" />
        {t('absenceReasons.form.balanceTitle')}
      </div>
    </AccordionTrigger>
    <AccordionContent className="space-y-4 pt-4">
      <div className="flex items-center space-x-2">
        <Checkbox
          id="has_balance"
          checked={formData.has_balance}
          onCheckedChange={(checked) => {
            updateField('has_balance', checked);
            if (!checked) updateField('default_annual_balance', null);
          }}
        />
        <Label htmlFor="has_balance" className="cursor-pointer">{t('absenceReasons.form.hasBalance')}</Label>
      </div>

      {formData.has_balance && (
        <>
          <div className="space-y-2">
            <Label htmlFor="default_annual_balance">
              {t('absenceReasons.form.defaultAnnualBalance', { unit: unitLabel })}
            </Label>
            <Input
              id="default_annual_balance"
              type="number"
              min="0"
              step="0.5"
              value={formData.default_annual_balance || ''}
              onChange={(e) =>
                updateField('default_annual_balance', e.target.value ? parseFloat(e.target.value) : null)
              }
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="balance_renewal_period">{t('absenceReasons.form.renewalPeriod')}</Label>
            <Select
              value={formData.balance_renewal_period}
              onValueChange={(v) => updateField('balance_renewal_period', v)}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="YEARLY">{t('absenceReasons.form.yearly')}</SelectItem>
                <SelectItem value="MONTHLY">{t('absenceReasons.form.monthly')}</SelectItem>
                <SelectItem value="QUARTERLY">{t('absenceReasons.form.quarterly')}</SelectItem>
                <SelectItem value="NEVER">{t('absenceReasons.form.never')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="allow_negative_balance"
              checked={formData.allow_negative_balance}
              onCheckedChange={(checked) => updateField('allow_negative_balance', checked)}
            />
            <Label htmlFor="allow_negative_balance" className="cursor-pointer">
              {t('absenceReasons.form.allowNegativeBalance')}
            </Label>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="carry_over_unused_balance"
              checked={formData.carry_over_unused_balance}
              onCheckedChange={(checked) => {
                updateField('carry_over_unused_balance', checked);
                if (!checked) updateField('max_carry_over_days', null);
              }}
            />
            <Label htmlFor="carry_over_unused_balance" className="cursor-pointer">
              {t('absenceReasons.form.carryOverUnused')}
            </Label>
          </div>

          {formData.carry_over_unused_balance && (
            <div className="space-y-2">
              <Label htmlFor="max_carry_over_days">
                {t('absenceReasons.form.maxCarryOver', { unit: unitLabel })}
              </Label>
              <Input
                id="max_carry_over_days"
                type="number"
                min="0"
                step="0.5"
                value={formData.max_carry_over_days || ''}
                onChange={(e) =>
                  updateField('max_carry_over_days', e.target.value ? parseFloat(e.target.value) : null)
                }
                placeholder={t('absenceReasons.form.unlimited')}
              />
            </div>
          )}
        </>
      )}
    </AccordionContent>
  </AccordionItem>
  );
};

export default AbsenceBalanceAccordion;
